/**
 * FavoriteButton — Web implementation
 *
 * Uses CSS keyframes + SVG path, exactly matching the HTML reference.
 * Metro picks this file over FavoriteButton.tsx on web.
 *
 * Classes:
 *   .active    → visual state (red heart)
 *   .animating → triggers heartPop + particle burst (removed after 700ms)
 */
import React, { useRef, useEffect } from 'react'
import type { FavoriteButtonProps } from './FavoriteButton'

// Inject keyframes + classes once into <head>
let injected = false
function injectCSS() {
  if (injected || typeof document === 'undefined') return
  injected = true
  const style = document.createElement('style')
  style.textContent = `
    .fliisi-fav-btn {
      background: none;
      border: none;
      cursor: pointer;
      padding: 4px;
      line-height: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      -webkit-tap-highlight-color: transparent;
    }
    .fliisi-fav-heart {
      fill: transparent;
      stroke: #a0a0a0;
      stroke-width: 2;
      stroke-linecap: round;
      stroke-linejoin: round;
      transition: stroke 0.3s ease, fill 0.3s ease;
      transform-origin: center;
      animation: fliisiFavIdle 2.5s ease-in-out infinite;
    }
    .fliisi-fav-btn.active .fliisi-fav-heart {
      stroke: #ff3b30;
      fill: #ff3b30;
    }
    .fliisi-fav-btn.animating .fliisi-fav-heart {
      animation: fliisiFavPop 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
    }
    .fliisi-fav-particle {
      fill: #ff3b30;
      opacity: 0;
      transform-origin: 16px 16px;
    }
    .fliisi-fav-btn.animating .fliisi-fav-p1 { animation: fliisiFavP1 0.6s ease-out forwards; }
    .fliisi-fav-btn.animating .fliisi-fav-p2 { animation: fliisiFavP2 0.6s ease-out forwards; }
    .fliisi-fav-btn.animating .fliisi-fav-p3 { animation: fliisiFavP3 0.6s ease-out forwards; }
    .fliisi-fav-btn.animating .fliisi-fav-p4 { animation: fliisiFavP4 0.6s ease-out forwards; }
    .fliisi-fav-btn.animating .fliisi-fav-p5 { animation: fliisiFavP5 0.6s ease-out forwards; }
    .fliisi-fav-btn.animating .fliisi-fav-p6 { animation: fliisiFavP6 0.6s ease-out forwards; }

    @keyframes fliisiFavIdle {
      0%, 100% { transform: scale(1); }
      50%       { transform: scale(1.08); stroke: #888; }
    }
    @keyframes fliisiFavPop {
      0%   { transform: scale(1); }
      40%  { transform: scale(1.3); }
      60%  { transform: scale(0.9); }
      80%  { transform: scale(1.1); }
      100% { transform: scale(1); }
    }
    @keyframes fliisiFavP1 {
      0%   { transform: translate(0px, 0px)   scale(0); opacity: 1; }
      100% { transform: translate(0px, -25px) scale(1); opacity: 0; }
    }
    @keyframes fliisiFavP2 {
      0%   { transform: translate(0px, 0px)    scale(0);   opacity: 1; }
      100% { transform: translate(22px, -12px) scale(1.2); opacity: 0; }
    }
    @keyframes fliisiFavP3 {
      0%   { transform: translate(0px, 0px)   scale(0);   opacity: 1; }
      100% { transform: translate(22px, 12px) scale(0.8); opacity: 0; }
    }
    @keyframes fliisiFavP4 {
      0%   { transform: translate(0px, 0px)  scale(0); opacity: 1; }
      100% { transform: translate(0px, 25px) scale(1); opacity: 0; }
    }
    @keyframes fliisiFavP5 {
      0%   { transform: translate(0px, 0px)    scale(0);   opacity: 1; }
      100% { transform: translate(-22px, 12px) scale(1.1); opacity: 0; }
    }
    @keyframes fliisiFavP6 {
      0%   { transform: translate(0px, 0px)     scale(0);   opacity: 1; }
      100% { transform: translate(-22px, -12px) scale(0.9); opacity: 0; }
    }
  `
  document.head.appendChild(style)
}

export function FavoriteButton({ active = false, onPress, size = 24 }: FavoriteButtonProps) {
  injectCSS()

  const btnRef = useRef<HTMLButtonElement>(null)
  const prevActive = useRef(active)

  // Sync .active class to prop (without triggering animation)
  useEffect(() => {
    const btn = btnRef.current
    if (!btn) return
    btn.classList.toggle('active', active)
  }, [active])

  // Trigger burst animation only on false→true transition
  useEffect(() => {
    const btn = btnRef.current
    if (!btn) return
    if (active && !prevActive.current) {
      btn.classList.remove('animating')
      void btn.offsetWidth // force reflow to restart CSS animation
      btn.classList.add('animating')
      const timer = setTimeout(() => btn.classList.remove('animating'), 700)
      prevActive.current = active
      return () => clearTimeout(timer)
    }
    prevActive.current = active
  }, [active])

  return (
    <button
      ref={btnRef}
      className={`fliisi-fav-btn${active ? ' active' : ''}`}
      onClick={onPress as any}
      aria-label="Add to favorites"
      aria-pressed={active}
    >
      <svg
        viewBox="0 0 32 32"
        width={size}
        height={size}
        style={{ overflow: 'visible' }}
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Particles — centered at (16,16), burst outward on .animating */}
        <circle className="fliisi-fav-particle fliisi-fav-p1" cx="16" cy="16" r="2.5" />
        <circle className="fliisi-fav-particle fliisi-fav-p2" cx="16" cy="16" r="2"   />
        <circle className="fliisi-fav-particle fliisi-fav-p3" cx="16" cy="16" r="1.5" />
        <circle className="fliisi-fav-particle fliisi-fav-p4" cx="16" cy="16" r="2.5" />
        <circle className="fliisi-fav-particle fliisi-fav-p5" cx="16" cy="16" r="2"   />
        <circle className="fliisi-fav-particle fliisi-fav-p6" cx="16" cy="16" r="1.5" />

        {/* Heart path — exact path from HTML reference */}
        <path
          className="fliisi-fav-heart"
          d="M16,28.261c-0.347,0-0.682-0.169-0.887-0.457c-2.885-4.04-12.113-17.143-12.113-21.201
             C3,3.585,5.432,1.213,8.472,1.213c1.789,0,3.468,0.854,4.551,2.333l2.977,4.062l2.977-4.062
             c1.083-1.479,2.762-2.333,4.551-2.333C26.568,1.213,29,3.585,29,6.604c0,4.058-9.229,17.161-12.113,21.201
             C16.682,28.092,16.347,28.261,16,28.261z"
        />
      </svg>
    </button>
  )
}
